// SelectionSurfacesBlock — the per-instance material picker that lives in
// the single-menu "on selection" popup. Reads the host's currently
// selected instance, lazily fetches that product's surface detail (cached
// by productId), and renders one column per non-locked surface: surface
// label at top, swatches as a horizontal row below.
//
// Differs from MaterialCollectionsBlock: that block reads from the active
// series (one set of finishes for every product in the series). This block
// reads from the currently-selected instance's product detail, so the
// picker reflects exactly the surfaces this product has — including
// surfaces unique to this product within the series.
//
// Renders nothing when no selection.

import { useEffect, useState } from "react";
import { fetchProductDetail } from "../api/catalogClient";
import type { RestrictedMaterialsEntry } from "../../../rules";
import type { SceneProductDetailResponse } from "../types";
import type { BlockHostContext, BlockUniversalProps } from "./types";

/** First restriction entry a material fails, or undefined when it passes all.
 *  Restrictions union (see src/rules/overlay.ts): a swatch must pass every
 *  entry — blocked lists exclude it, allowed lists must include it. The
 *  failing entry is returned (not just a boolean) so the swatch can carry the
 *  rule's authored message id in a data attribute. */
function failingRestriction(
  entries: RestrictedMaterialsEntry[],
  materialId: string,
): RestrictedMaterialsEntry | undefined {
  return entries.find(
    r =>
      (r.blockedMaterialIds?.includes(materialId) ?? false) ||
      (r.allowedMaterialIds != null && !r.allowedMaterialIds.includes(materialId)),
  );
}

export interface SelectionSurfacesBlockProps extends BlockUniversalProps {
  host: BlockHostContext;
}

export function SelectionSurfacesBlock({
  host,
  themeVariant,
  className,
}: SelectionSurfacesBlockProps) {
  const { selectedInstance, detailCache, detailCacheVersion, onCacheDetail, baseUrl, lang, onPickMaterial } = host;
  const productId = selectedInstance?.productId ?? null;

  const [detail, setDetail] = useState<SceneProductDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Hovered material id per surface. Drives the "Wood – Smoked Oak" hint
  // beside the row label; mirrors the main-menu MaterialCollectionsBlock
  // hover behaviour for visual parity.
  const [hovered, setHovered] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!productId) {
      setDetail(null);
      return;
    }
    const cached = detailCache.get(productId);
    if (cached) {
      setDetail(cached);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchProductDetail({ baseUrl, productId, lang, signal: ctrl.signal })
      .then(d => {
        onCacheDetail(d);
        setDetail(d);
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setError(err.message ?? String(err));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
    // detailCache / onCacheDetail are stable refs from the host; intentionally
    // not in deps so a cache write does not re-trigger the fetch. `lang` IS in
    // deps: a language switch must re-fetch this product's translated
    // surface/material names. `detailCacheVersion` is in deps too so that after
    // the tool clears the shared cache on a lang change (a parent effect, which
    // runs *after* this child effect), the version bump re-runs this effect —
    // by then the cache is empty, so it fetches fresh instead of reading a
    // stale-language entry. Without it, effect ordering could show old names.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, baseUrl, lang, detailCacheVersion]);

  if (!selectedInstance) return null;

  const pickableSurfaces = detail?.surfaces.filter(s => !s.locked) ?? [];

  // Rules-engine overlay bucket for the selection (undefined when no rules
  // fired on it / no rules loaded — everything below then renders
  // unconstrained). Forced and rule-locked surfaces disable their whole row;
  // restricted materials grey out individual swatches; flags targeting this
  // instance (or one of its surfaces) render as a banner list above the rows.
  const instanceOverlay = host.rulesOverlay?.instances[selectedInstance.instanceId];
  const flags = instanceOverlay?.flags ?? [];

  return (
    // Reuses the .sd-block-material-collections__* class names so the
    // existing layout-scoped styles (circular swatch, caption beneath,
    // selected-ring treatment) apply automatically and the on-selection
    // popup matches the main finishes picker pixel-for-pixel. The wrapper
    // also carries .sd-block-selection-surfaces so the popup-specific row
    // layout (horizontal sections, left-aligned headings) can scope its
    // own rules without colliding with the main-menu vertical stack.
    <div
      className={
        "sd-block-selection-surfaces sd-block-material-collections" +
        (className ? " " + className : "")
      }
      data-variant={themeVariant}
    >
      {loading && (
        <p className="sd-block-material-collections__state">Loading materials…</p>
      )}
      {error && (
        <p className="sd-block-material-collections__state sd-block-material-collections__state--error">
          {error}
        </p>
      )}
      {flags.length > 0 && (
        <div className="sd-rule-flags">
          {flags.map((flag, i) => {
            // The authored explanation arrives server-resolved as messageText
            // (RuleEmbedEndpoints ?lang=). When absent (no message authored /
            // DVar empty) we fall back to the generic severity label; the raw
            // DVar id keeps riding data-rule-message either way.
            const label =
              flag.severity === "error" ? "Configuration error"
              : flag.severity === "warning" ? "Configuration warning"
              : "Configuration note";
            const flaggedKey = flag.target.type === "surface" ? flag.target.surfaceKey : null;
            const surfaceLabel = flaggedKey
              ? detail?.surfaces.find(s => s.surfaceKey === flaggedKey)?.surfaceLabel ?? flaggedKey
              : null;
            return (
              <div
                key={`${flag.ruleId}-${i}`}
                className={`sd-rule-flag sd-rule-flag--${flag.severity}`}
                role={flag.severity === "error" ? "alert" : "status"}
                data-rule-message={flag.messageDvarId ?? undefined}
              >
                {flag.messageText ?? `${label}${surfaceLabel ? ` — ${surfaceLabel}` : ""}`}
              </div>
            );
          })}
        </div>
      )}
      {!loading && !error && pickableSurfaces.map(surface => {
        const forcedPick = instanceOverlay?.forcedPicks[surface.surfaceKey];
        // Forced surfaces are locked too: the rule owns the pick, so offering
        // the row would only invite taps the evaluator immediately overrides.
        const ruleLocked =
          forcedPick != null || instanceOverlay?.lockedSurfaces[surface.surfaceKey] != null;
        const restrictions = instanceOverlay?.restrictedMaterials[surface.surfaceKey] ?? [];
        // Effective pick: a forced pick wins over the canonical one so the
        // selected ring matches what the viewer actually shows.
        const current =
          forcedPick?.materialId ??
          selectedInstance.materialPicks[surface.surfaceKey] ?? surface.defaultMaterialId ?? null;
        // While hovering a swatch, the surface-label header text itself swaps to
        // the hovered material's name (no appended chip); reverts to the surface
        // label on mouse-out. Mirrors the main-menu MaterialCollectionsBlock.
        const hoveredName = hovered[surface.surfaceKey]
          ? surface.materials.find(m => m.id === hovered[surface.surfaceKey])?.name ?? ""
          : "";
        const headerText = hoveredName || surface.surfaceLabel;
        return (
          <section
            key={surface.surfaceKey}
            className={
              "sd-block-material-collections__row" +
              (ruleLocked ? " sd-block-material-collections__row--locked" : "")
            }
            aria-disabled={ruleLocked || undefined}
            title={ruleLocked ? "Locked by a configuration rule" : undefined}
          >
            <header className="sd-block-material-collections__row-header">
              <span className="sd-block-material-collections__row-label">{headerText}</span>
              {ruleLocked && (
                <svg
                  className="sd-block-material-collections__row-lock"
                  width="10" height="10" viewBox="0 0 24 24" aria-hidden="true"
                >
                  <path
                    d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5zm-3 8V7a3 3 0 1 1 6 0v3H9z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </header>
            <div className="sd-block-material-collections__swatches">
              {surface.materials.map(m => {
                const isSelected = current === m.id;
                // Restricted = fails a restrictMaterials entry. Locked rows
                // disable every swatch regardless; the restricted styling only
                // shows on otherwise-live rows so the two states read apart.
                const restriction = ruleLocked ? undefined : failingRestriction(restrictions, m.id);
                const disabled = ruleLocked || restriction != null;
                // A restricted swatch prefers the rule's authored, server-
                // translated message (messageText); generic fallback otherwise.
                const title = ruleLocked
                  ? "Locked by a configuration rule"
                  : restriction
                    ? restriction.messageText ?? "Not available with the current configuration"
                    : m.name;
                return (
                  <div
                    key={m.id}
                    className={
                      "sd-block-material-collections__swatch-item" +
                      (isSelected
                        ? " sd-block-material-collections__swatch-item--selected"
                        : "")
                    }
                  >
                    <button
                      type="button"
                      className={
                        "sd-block-material-collections__swatch" +
                        (isSelected ? " sd-block-material-collections__swatch--selected" : "") +
                        (restriction ? " sd-block-material-collections__swatch--restricted" : "")
                      }
                      onClick={() => onPickMaterial(surface.surfaceKey, m.id)}
                      onMouseEnter={() => setHovered(prev => ({ ...prev, [surface.surfaceKey]: m.id }))}
                      onMouseLeave={() =>
                        setHovered(prev => {
                          if (prev[surface.surfaceKey] !== m.id) return prev;
                          const next = { ...prev };
                          delete next[surface.surfaceKey];
                          return next;
                        })
                      }
                      title={title}
                      aria-label={m.name}
                      aria-pressed={isSelected}
                      disabled={disabled}
                      // The failing rule's raw DVar id stays on a data
                      // attribute for hosts/tests; the resolved text already
                      // rides the title above.
                      data-rule-message={restriction?.messageDvarId ?? undefined}
                    >
                      {m.swatchUrl ? (
                        <img src={m.swatchUrl} alt="" draggable={false} />
                      ) : (
                        <span className="sd-block-material-collections__swatch-blank" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
